import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, TextArea } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface CutItem {
  id: string;
  item: string;
  reason: string;
}

export default function TradeoffsAntiGoalsPage() {
  const [cutList, setCutList] = useState<CutItem[]>([
    { id: '1', item: '', reason: '' },
  ]);
  const [guardrails, setGuardrails] = useState('');
  const [broadcast, setBroadcast] = useState('');

  const updateCutItem = (id: string, field: keyof CutItem, value: string) => {
    setCutList(cutList.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addCutItem = () => {
    if (cutList.length < 5) {
      setCutList([...cutList, { id: Date.now().toString(), item: '', reason: '' }]);
    }
  };

  return (
    <TaskPage
      phase="prioritize"
      title="Trade-offs & Anti-goals"
      pageNumber={5}
      instruction="Say no on purpose. Define what we're not doing and why. Set guardrails to prevent scope creep."
      whyItMatters="Writing 'not doing' in plain language reduces rework and support burden; quick clarity checks catch confusing phrasing before rollout."
      coachRail={{
        timing: "10 minutes",
        tips: "Be explicit about what's not happening. This prevents thrash and confusion.",
        debrief: "Who needs to know what we're not doing? Is it clear?"
      }}
    >
      <div className="space-y-6">
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-6">
          <h3 className="text-lg font-semibold text-[var(--coral)] mb-4">Cut List (3-5 items)</h3>
          <p className="text-sm text-[var(--coral)] mb-4">We are not doing… (and why)</p>
          <div className="space-y-3">
            {cutList.map((cut) => (
              <div key={cut.id} className="bg-[var(--bg-raised)] border border-[var(--coral)] rounded p-3">
                <TextInput
                  value={cut.item}
                  onChange={(e) => updateCutItem(cut.id, 'item', e.target.value)}
                  placeholder="What we're NOT doing"
                  className="mb-2"
                />
                <TextInput
                  value={cut.reason}
                  onChange={(e) => updateCutItem(cut.id, 'reason', e.target.value)}
                  placeholder="Why we're not doing it"
                />
              </div>
            ))}
            {cutList.length < 5 && (
              <button
                onClick={addCutItem}
                className="w-full py-2 px-4 border-2 border-dashed border-[var(--coral)] text-[var(--coral)] hover:border-[var(--coral)] hover:text-[var(--coral)] transition-colors"
              >
                + Add to cut list (max 5)
              </button>
            )}
          </div>
        </div>

        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-6">
          <h3 className="text-lg font-semibold text-[var(--mustard)] mb-4">Guardrails</h3>
          <p className="text-sm text-[var(--mustard)] mb-4">We will not break these boundaries this cycle… (scope, quality bars, team load)</p>
          <TextArea
            label="Boundaries we won't break"
            value={guardrails}
            onChange={(e) => setGuardrails(e.target.value)}
            placeholder="What boundaries are we protecting? (scope, quality, team capacity, etc.)"
            rows={4}
          />
        </div>

        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-sec)] mb-4">Broadcast Box</h3>
          <p className="text-sm text-[var(--text-sec)] mb-4">Who needs to hear 'what's not happening' to prevent thrash?</p>
          <TextArea
            label="Stakeholders to notify"
            value={broadcast}
            onChange={(e) => setBroadcast(e.target.value)}
            placeholder="Who needs to know what we're NOT doing? (prevents confusion and requests)"
            rows={3}
          />
        </div>

        <Callout type="tip" phase="prioritize">
          <p className="text-sm">Be explicit about what's not happening. This prevents confusion, reduces rework, and manages expectations.</p>
        </Callout>
      </div>
    </TaskPage>
  );
}






